import type { ApiNode } from '@goalbuddy/client';
import type { NodeType } from '@goalbuddy/core';
import {
  Bot,
  CalendarDays,
  Clock,
  Handshake,
  Zap,
  CheckSquare,
  Flag,
  FolderKanban,
  Heading,
  HelpCircle,
  LayoutGrid,
  Lightbulb,
  List,
  Repeat,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui';
import { fmtDate } from '@/lib/utils';

export const TYPE_ICON: Record<NodeType, LucideIcon> = {
  area: LayoutGrid,
  goal: Target,
  result: Flag,
  task: CheckSquare,
  idea: Lightbulb,
  routine: Repeat,
  project: FolderKanban,
  section: Heading,
  list: List,
};

export const TYPE_COLOR: Record<NodeType, string> = {
  area: 'text-sky-600',
  goal: 'text-indigo-600',
  result: 'text-emerald-600',
  task: 'text-zinc-500',
  idea: 'text-amber-500',
  routine: 'text-pink-600',
  project: 'text-violet-600',
  section: 'text-zinc-400',
  list: 'text-teal-600',
};

export function TypeIcon({ type, size = 14 }: { type: NodeType; size?: number }) {
  const Icon = TYPE_ICON[type];
  return <Icon size={size} className={`shrink-0 ${TYPE_COLOR[type]}`} aria-label={type} />;
}

const isoDay = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(d);

export function dueInfo(
  due: string,
  done = false,
): { label: string; tone: 'danger' | 'warn' | 'default' | 'primary' } {
  const today = isoDay(new Date());
  const tomorrow = isoDay(new Date(Date.now() + 86_400_000));
  if (due === today) return { label: 'Today', tone: done ? 'default' : 'warn' };
  if (due === tomorrow) return { label: 'Tomorrow', tone: 'default' };
  if (due < today) {
    const days = Math.round(
      (new Date(`${today}T12:00:00`).getTime() - new Date(`${due}T12:00:00`).getTime()) / 86_400_000,
    );
    return { label: done ? fmtDate(due) : `${days}d overdue`, tone: done ? 'default' : 'danger' };
  }
  return { label: fmtDate(due), tone: 'default' };
}

/** Small chips for status, dates, assignee and flags. Always visible, never hover-only. */
export function NodeBadges({ node, assignee }: { node: ApiNode; assignee?: string | null }) {
  const due = node.due_date ? dueInfo(node.due_date, node.status === 'done') : null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {node.status !== 'active' && (
        <Badge tone={node.status === 'done' ? 'ok' : node.status === 'waiting' ? 'warn' : 'default'}>
          {node.status}
        </Badge>
      )}
      {node.priority === 1 && <Badge tone="danger">P1</Badge>}
      {due && (
        <Badge tone={due.tone}>
          <CalendarDays size={11} /> {due.label}
        </Badge>
      )}
      {node.scheduled_at && (
        <Badge>
          <Clock size={11} /> {fmtDate(node.scheduled_at)}
        </Badge>
      )}
      {node.is_next_action && (
        <Badge tone="primary">
          <Zap size={11} /> next
        </Badge>
      )}
      {node.buddy_focus && (
        <Badge tone="warn">
          <Handshake size={11} /> buddy
        </Badge>
      )}
      {node.assignee_type === 'agent' && (
        <Badge tone="agent">
          <Bot size={11} /> {assignee ?? 'agent'}
        </Badge>
      )}
      {node.context && <Badge>{node.context}</Badge>}
      {(node.open_question || node.needs_clarification) && (
        <Badge tone="warn" title={node.open_question ?? 'needs clarification'}>
          <HelpCircle size={11} /> question
        </Badge>
      )}
      {node.flags.includes('missing_completion_criterion') && <Badge tone="warn">no criterion</Badge>}
      {node.tags.map((t) => (
        <Badge key={t}>#{t}</Badge>
      ))}
    </div>
  );
}
